/* eslint-disable */
import React, { useState, useEffect, useRef } from "react";
import { supabase } from "../supabase";
import { showToast as globalToast } from "../Toast";
import { removeBackgroundViaEdge } from "./removeBgClient";

// ── Image helpers ─────────────────────────────────────────────────────────────
export function compressImage(file, maxW = 900, quality = 0.80) {
  return new Promise(resolve => {
    const reader = new FileReader();
    reader.onload = e => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, maxW / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale), h = Math.round(img.height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        const ctx2d = canvas.getContext("2d");
        ctx2d.fillStyle = "#ffffff";
        ctx2d.fillRect(0, 0, w, h);
        ctx2d.drawImage(img, 0, 0, w, h);
        const webp = canvas.toDataURL("image/webp", quality);
        if (webp.startsWith("data:image/webp")) {
          resolve(webp);
        } else {
          resolve(canvas.toDataURL("image/jpeg", quality));
        }
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
}

export async function downloadImageWithWhiteBg(src, filename) {
  try {
    const img = new Image();
    img.crossOrigin = "anonymous";
    await new Promise((res, rej) => { img.onload = res; img.onerror = rej; img.src = src; });
    const canvas = document.createElement("canvas");
    canvas.width = img.naturalWidth || img.width;
    canvas.height = img.naturalHeight || img.height;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0);
    const blob = await new Promise(r => canvas.toBlob(r, "image/jpeg", 0.92));
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || "image.jpg";
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch(e) {
    window.open(src, "_blank");
  }
}

export function isStorageUrl(src) { return typeof src === 'string' && src.startsWith('http'); }

export async function loadJSZip() {
  if (window.JSZip) return window.JSZip;
  return new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = 'https://cdnjs.cloudflare.com/ajax/libs/jszip/3.10.1/jszip.min.js';
    s.onload = () => resolve(window.JSZip);
    s.onerror = reject;
    document.head.appendChild(s);
  });
}

export async function fetchImageBlob(url) {
  if (!url || typeof url !== 'string') return null;
  const cleanUrl = url.split('?')[0];
  try {
    const resp = await fetch(cleanUrl, { mode: 'cors', cache: 'no-cache' });
    if (!resp.ok) throw new Error('fetch failed: ' + resp.status);
    const blob = await resp.blob();
    if (!blob || blob.size === 0) throw new Error('empty blob');
    return blob;
  } catch(e) {
    try {
      const resp2 = await fetch(cleanUrl + '?t=' + Date.now(), { mode: 'cors' });
      if (!resp2.ok) return null;
      const blob2 = await resp2.blob();
      return (blob2 && blob2.size > 0) ? blob2 : null;
    } catch(e2) {
      return null;
    }
  }
}

export async function downloadZip(zip, filename) {
  const blob = await zip.generateAsync({ type:'blob', compression:'DEFLATE', compressionOptions:{level:6} });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

export function storageSrc(url) {
  if (!url || typeof url !== 'string') return url;
  if (url.includes('supabase.co/storage/')) {
    const sep = url.includes('?') ? '&' : '?';
    return url + sep + 'v=' + (Math.floor(Date.now()/60000));
  }
  return url;
}

// ── Image Transform (DISABLED) ──────────────────────────────────────────────
export function transformUrl(url, displayWidth = 400, quality = 75) {
  if (!url || typeof url !== 'string') return url;
  return url.split('?')[0];
}

// ── Image Load Queue — max 6 concurrent loads ──────────────────────────────
const imgQueue = (() => {
  let active = 0;
  const MAX = 6;
  const waiting = [];
  return {
    request(priority) {
      return new Promise(resolve => {
        if (active < MAX || priority) { active++; resolve(); }
        else waiting.push(resolve);
      });
    },
    done() {
      active = Math.max(0, active - 1);
      if (waiting.length > 0 && active < MAX) {
        active++;
        waiting.shift()();
      }
    }
  };
})();

// ── SmartImg — queued loader with IntersectionObserver + priority ────────────
export function SmartImg({ src, alt, style, displayWidth = 400, noTransform = false, priority = false, ...props }) {
  const [status, setStatus] = React.useState('idle');
  const containerRef = React.useRef(null);
  const cleanupRef = React.useRef(null);
  const mountedRef = React.useRef(true);
  const MAX_RETRIES = 2;
  const RETRY_DELAYS = [1200, 3000];

  React.useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false; }; }, []);

  React.useEffect(() => {
    if (!src) { setStatus('error'); return; }
    setStatus('idle');
    if (cleanupRef.current) { cleanupRef.current(); cleanupRef.current = null; }

    const finalUrl = (!noTransform && displayWidth > 0) ? transformUrl(src, displayWidth, 75) : src;

    const startLoad = async (url, attempt) => {
      let released = false;
      const release = () => { if (!released) { released = true; imgQueue.done(); } };
      cleanupRef.current = release;

      await imgQueue.request(priority);
      if (!mountedRef.current) { release(); return; }
      setStatus('loading');

      const img = new Image();
      img.onload = () => { release(); if (mountedRef.current) setStatus('loaded'); };
      img.onerror = () => {
        release();
        if (!mountedRef.current) return;
        if (attempt < MAX_RETRIES) {
          setTimeout(() => { if (mountedRef.current) startLoad(url, attempt + 1); }, RETRY_DELAYS[attempt]);
        } else {
          setStatus('error');
        }
      };
      img.src = attempt > 0 ? url.split('?')[0] + '?_r=' + attempt : url;
    };

    if (priority) {
      startLoad(finalUrl, 0);
      return () => { if (cleanupRef.current) cleanupRef.current(); };
    }

    const observer = new IntersectionObserver(
      entries => { if (entries[0].isIntersecting) { observer.disconnect(); startLoad(finalUrl, 0); } },
      { rootMargin: '300px' }
    );
    if (containerRef.current) observer.observe(containerRef.current);
    return () => { observer.disconnect(); if (cleanupRef.current) cleanupRef.current(); };
  }, [src, displayWidth, noTransform, priority]);

  const imgStyle = style || {};

  if (status === 'idle' || status === 'loading') {
    return React.createElement('div', {
      ref: containerRef,
      style: Object.assign({}, imgStyle, { background: 'var(--surface-warm)', overflow: 'hidden', position: 'relative' }),
      className: props.className,
    },
      React.createElement('div', {
        style: {
          position: 'absolute', inset: 0,
          background: 'linear-gradient(90deg, var(--surface-warm) 25%, #e8e3dc 50%, var(--surface-warm) 75%)',
          backgroundSize: '200% 100%',
          animation: 'clb-shimmer 1.4s infinite linear',
        }
      })
    );
  }

  if (status === 'error') {
    return React.createElement('div', {
      style: Object.assign({}, imgStyle, { background: '#fafaf8', display: 'flex', alignItems: 'center', justifyContent: 'center' }),
      className: props.className,
    },
      // SmartImg error-state skeleton stroke (#d4cfc8) — intentional one-off icon grey, not in design-system scope
      React.createElement('svg', { width: 28, height: 28, viewBox: '0 0 24 24', fill: 'none', stroke: '#d4cfc8', strokeWidth: 1.2 },
        React.createElement('rect', { x: 3, y: 3, width: 18, height: 18, rx: 2 }),
        React.createElement('circle', { cx: 8.5, cy: 8.5, r: 1.5 }),
        React.createElement('polyline', { points: '21,15 16,10 5,21' })
      )
    );
  }

  return React.createElement('img', Object.assign({}, props, {
    src: (!noTransform && displayWidth > 0) ? transformUrl(src, displayWidth, 75) : src,
    alt: alt || '',
    style: Object.assign({}, imgStyle, { opacity: 1, transition: 'opacity 0.3s ease' }),
    loading: 'lazy',
    onClick: props.onClick,
  }));
}

// ── Content-Aware Image Centering ──────────────────────────────────────────
function getContentBounds(img) {
  try {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    if (!canvas.width || !canvas.height) return null;
    ctx.drawImage(img, 0, 0);
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const d = imageData.data;
    let minX = canvas.width, minY = canvas.height, maxX = 0, maxY = 0;
    const threshold = 240;
    for (let y = 0; y < canvas.height; y += 2) {
      for (let x = 0; x < canvas.width; x += 2) {
        const i = (y * canvas.width + x) * 4;
        const r = d[i], g = d[i+1], b = d[i+2], a = d[i+3];
        if (a < 10 || (r > threshold && g > threshold && b > threshold)) continue;
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
    const cw = maxX - minX, ch = maxY - minY;
    if (cw < canvas.width * 0.1 || ch < canvas.height * 0.1) return null;
    return {
      centerX: (minX + maxX) / 2 / canvas.width * 100,
      centerY: (minY + maxY) / 2 / canvas.height * 100,
    };
  } catch(e) { return null; }
}

const _contentCenterCache = {};
export function computeContentCenter(imgEl, src) {
  if (!src || _contentCenterCache[src] !== undefined) return _contentCenterCache[src] || null;
  _contentCenterCache[src] = null;
  const run = () => {
    try {
      const bounds = getContentBounds(imgEl);
      if (bounds) _contentCenterCache[src] = bounds;
    } catch(e) { /* ignore */ }
  };
  if (typeof requestIdleCallback === 'function') requestIdleCallback(run);
  else setTimeout(run, 50);
  return null;
}

export async function base64ToBlob(base64) {
  const byteStr = atob(base64.split(',')[1]);
  const mime = base64.split(',')[0].split(':')[1].split(';')[0];
  const arr = new Uint8Array(byteStr.length);
  for (let i = 0; i < byteStr.length; i++) arr[i] = byteStr.charCodeAt(i);
  return new Blob([arr], { type: mime });
}

// ── Dual-pipeline upload ─────────────────────────────────────────────────────
export async function uploadImageToStorage(src) {
  try {
    const uid = Date.now() + '_' + Math.random().toString(36).slice(2);

    async function srcToBlob(imgSrc, mimeType, quality) {
      return new Promise(function(resolve) {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        let objUrl = null;
        img.onload = function() {
          const canvas = document.createElement('canvas');
          canvas.width = img.naturalWidth || img.width;
          canvas.height = img.naturalHeight || img.height;
          const ctx = canvas.getContext('2d');
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(img, 0, 0);
          if (objUrl) URL.revokeObjectURL(objUrl);
          canvas.toBlob(resolve, mimeType, quality);
        };
        img.onerror = function() { if (objUrl) URL.revokeObjectURL(objUrl); resolve(null); };
        if (typeof imgSrc === 'string') {
          img.src = imgSrc;
        } else {
          objUrl = URL.createObjectURL(imgSrc);
          img.src = objUrl;
        }
      });
    }

    let displayBlob = null;
    let displayExt = 'jpg';
    if (typeof src === 'string' && src.startsWith('data:image/webp')) {
      displayBlob = await base64ToBlob(src);
      displayExt = 'webp';
    } else if (typeof src === 'string' && src.startsWith('data:')) {
      displayBlob = await base64ToBlob(src);
      displayExt = 'jpg';
    } else {
      displayBlob = await srcToBlob(src, 'image/jpeg', 0.82);
      displayExt = 'jpg';
    }
    if (!displayBlob || displayBlob.size < 100) throw new Error('Display blob empty');

    const displayFileName = uid + '.' + displayExt;
    const displayResult = await supabase.storage.from('item-images').upload(displayFileName, displayBlob, {
      contentType: displayExt === 'webp' ? 'image/webp' : 'image/jpeg',
      cacheControl: '31536000',
      upsert: false,
    });
    if (displayResult.error) throw displayResult.error;
    const displayUrl = supabase.storage.from('item-images').getPublicUrl(displayResult.data.path).data.publicUrl;

    const originalFileName = 'originals/' + uid + '_orig.jpg';
    srcToBlob(src, 'image/jpeg', 0.95).then(function(origBlob) {
      if (!origBlob || origBlob.size < 100) return;
      supabase.storage.from('item-images').upload(originalFileName, origBlob, {
        contentType: 'image/jpeg',
        cacheControl: '31536000',
        upsert: false,
      }).catch(function(e) { console.warn('Original upload failed (non-critical):', e); });
    });

    return displayUrl;
  } catch(e) {
    console.error('Storage upload failed:', e);
    return null;
  }
}

export async function deleteStorageImages(images) {
  if (!images || !images.length) return;
  const paths = images.filter(isStorageUrl).map(url => { const p = url.split('/item-images/'); return p.length >= 2 ? decodeURIComponent(p[1]) : null; }).filter(Boolean);
  if (paths.length > 0) await supabase.storage.from('item-images').remove(paths);
}

/**
 * Brief M — pure helper that runs the background-removal pipeline against a
 * single image URL (or data URL) and returns the new background-removed URL.
 *
 * Pipeline:
 *   1. fetch image source → Blob (handles both Storage URLs and base64 dataURLs)
 *   2. removeBackgroundViaEdge(blob) → Blob with transparent background
 *   3. read blob → base64 dataURL
 *   4. uploadImageToStorage(base64) → new Storage URL
 *   5. (optional) deleteStorageImages([oldUrl]) — only when caller opts in
 *      AND the original is a Storage URL (skips for data URLs)
 *
 * Pure async function — no React state, no toasts. Caller owns:
 *   - Loading-state UI
 *   - Error surfacing (this fn re-throws — caller catches)
 *   - DB write of the new URL into wherever it belongs
 *
 * Mirrors the inline pipeline at imageUtils.js:486-513 (handleRemoveBg)
 * which is tightly coupled to a React component scope (imgsRef.current /
 * setRemovingBg / onChange). Convention #122 — single canonical pipeline;
 * callers (the inline handleRemoveBg AND V2's useFilesV2.removeBg) both
 * delegate here.
 *
 * @param {string} imageUrl - Storage URL or base64 dataURL
 * @param {object} [options]
 * @param {boolean} [options.deleteOriginal=false] - When true AND original is
 *   a Storage URL, delete the orphan after the new URL is uploaded.
 * @returns {Promise<string>} New URL (Storage URL or fallback base64 dataURL)
 * @throws if any step fails — caller is responsible for catch + toast.
 */
export async function removeBgForUrl(imageUrl, { deleteOriginal = false } = {}) {
  if (!imageUrl) throw new Error('removeBgForUrl: missing imageUrl');

  // 1. Source → Blob
  let blob;
  if (isStorageUrl(imageUrl)) {
    const res = await fetch(imageUrl);
    if (!res.ok) throw new Error(`removeBgForUrl: fetch failed (${res.status})`);
    blob = await res.blob();
  } else if (typeof imageUrl === 'string' && imageUrl.startsWith('data:')) {
    const [head, b64] = imageUrl.split(',');
    if (!b64) throw new Error('removeBgForUrl: malformed data URL');
    const mime = head.split(':')[1].split(';')[0];
    const byteStr = atob(b64);
    const arr = new Uint8Array(byteStr.length);
    for (let i = 0; i < byteStr.length; i++) arr[i] = byteStr.charCodeAt(i);
    blob = new Blob([arr], { type: mime });
  } else {
    throw new Error('removeBgForUrl: unsupported imageUrl format');
  }

  // 2. BG removal via Edge Function
  const resultBlob = await removeBackgroundViaEdge(blob);
  if (!resultBlob) throw new Error('removeBgForUrl: edge function returned empty');

  // 3. Blob → base64 dataURL
  const base64 = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload  = (e) => resolve(e.target.result);
    reader.onerror = () => reject(new Error('removeBgForUrl: FileReader failed'));
    reader.readAsDataURL(resultBlob);
  });

  // 4. Upload to Storage → new URL (fallback to base64 if upload fails)
  const newUrl = await uploadImageToStorage(base64);
  const resolvedUrl = newUrl || base64;

  // 5. Optional: delete original Storage object (orphan cleanup)
  if (deleteOriginal && isStorageUrl(imageUrl)) {
    try {
      await deleteStorageImages([imageUrl]);
    } catch (e) {
      // Non-fatal — new URL is already uploaded. Caller can still use it.
      if (process.env.NODE_ENV !== 'production') {
        console.warn('[removeBgForUrl] orphan delete failed (non-fatal):', e?.message || e);
      }
    }
  }

  return resolvedUrl;
}

// SYS-10: the call itself lives in removeBgClient.js (signed-in user's token).
export { removeBackgroundViaEdge };

export function getPixel(data, x, y, W) {
  const i = (y * W + x) * 4;
  return [data[i], data[i+1], data[i+2]];
}

// ── ImageGallery ──────────────────────────────────────────────────────────────
export function ImageGallery({ images, assignedTo, onClose, T }) {
  const [active, setActive] = useState(0);
  const imgs = images || [];
  return (
    <div style={{ position:"relative", background:T.mainBg }}>
      <div style={{ height:200, display:"flex", alignItems:"center", justifyContent:"center", overflow:"hidden" }}>
        {imgs.length > 0
          ? <SmartImg src={imgs[Math.min(active, imgs.length-1)]} displayWidth={500} alt="" style={{ width:"100%", height:"100%", objectFit:"contain", background:"var(--card-bg)" }} />
          : <span style={{ fontSize:68 }}>👓</span>}
      </div>
      {imgs.length > 1 && (
        <>
          <button onClick={() => setActive(i => (i-1+imgs.length)%imgs.length)}
            style={{ position:"absolute", left:8, top:90, transform:"translateY(-50%)", background:"rgba(0,0,0,0.5)", border:"none", color:"var(--card-bg)", width:30, height:30, borderRadius:'var(--radius-pill)', cursor:"pointer", fontSize:'var(--text-lg)', fontWeight:800 }}>‹</button>
          <button onClick={() => setActive(i => (i+1)%imgs.length)}
            style={{ position:"absolute", right:8, top:90, transform:"translateY(-50%)", background:"rgba(0,0,0,0.5)", border:"none", color:"var(--card-bg)", width:30, height:30, borderRadius:'var(--radius-pill)', cursor:"pointer", fontSize:'var(--text-lg)', fontWeight:800 }}>›</button>
          <div style={{ display:"flex", gap:'var(--space-xs)', padding:"var(--space-sm) var(--space-md)", background:T.mainBg, justifyContent:"center" }}>
            {imgs.map((src, i) => (
              <div key={i} onClick={() => setActive(i)}
                style={{ width:44, height:34, borderRadius:'var(--radius-md)', overflow:"hidden", cursor:"pointer", border:`var(--border-thick) solid ${i===active?T.accent:T.border}` }}>
                <SmartImg src={src} displayWidth={60} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }} />
              </div>
            ))}
          </div>
        </>
      )}
      <div style={{ position:"absolute", top:10, right:10 }}>
        <span style={{ background:"rgba(0,0,0,0.55)", color:"var(--card-bg)", fontSize:'var(--text-xs)', fontWeight:'var(--weight-bold)', padding:"var(--space-2xs) var(--space-md)", borderRadius:'var(--radius-pill)' }}>
          {assignedTo ? `👤 ${assignedTo}` : "🏬 In Warehouse"}
        </span>
      </div>
      <button onClick={onClose} style={{ position:"absolute", top:10, left:10, background:"var(--card-bg)", border:"var(--border-med) solid #e2e8f0", borderRadius:'var(--radius-pill)', width:28, height:28, cursor:"pointer", fontSize:'var(--text-base)', color:"#64748b", fontWeight:800 }}>×</button>
    </div>
  );
}

// ── MultiImageUpload ──────────────────────────────────────────────────────────
export function MultiImageUpload({ images, onChange, maxImages = 10, onUploadingChange }) {
  const ref = useRef();
  const imgs = Array.isArray(images) ? images : (images ? [images] : []);
  const [drag, setDrag] = useState(false);
  const [removingBg, setRemovingBg] = useState(null);
  const [bgStatus, setBgStatus] = useState("");
  const [uploading, setUploading] = useState(false);
  const [toast, setToast] = useState("");
  const [hoverIdx, setHoverIdx] = useState(-1);
  const imgsRef = useRef(imgs);
  imgsRef.current = imgs;

  const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/webp"];
  const MAX_SIZE = 10 * 1024 * 1024;

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(""), 2500); };

  const processFiles = async (fileList) => {
    const current = imgsRef.current;
    const remaining = maxImages - current.length;
    if (remaining <= 0) { showToast("MAXIMUM 10 IMAGES"); return; }
    const files = Array.from(fileList).filter(f => {
      if (!ALLOWED_TYPES.includes(f.type)) return false;
      if (f.size > MAX_SIZE) return false;
      return true;
    }).slice(0, remaining);
    if (files.length === 0) return;
    if (Array.from(fileList).length > remaining) showToast("MAXIMUM 10 IMAGES");
    setUploading(true);
    if (onUploadingChange) onUploadingChange(true);
    const urls = [];
    for (const f of files) {
      const compressed = await compressImage(f);
      const url = await uploadImageToStorage(compressed);
      urls.push(url || compressed);
    }
    onChange([...current, ...urls].slice(0, maxImages));
    setUploading(false);
    if (onUploadingChange) onUploadingChange(false);
  };

  useEffect(() => {
    const onPaste = async (e) => {
      if (imgsRef.current.length >= maxImages) { showToast("MAXIMUM 10 IMAGES"); return; }
      const items = Array.from(e.clipboardData?.items || []);
      const imgItems = items.filter(it => it.type.startsWith("image/"));
      if (!imgItems.length) return;
      e.preventDefault();
      const files = imgItems.map(it => it.getAsFile()).filter(Boolean);
      await processFiles(files);
    };
    document.addEventListener("paste", onPaste);
    return () => document.removeEventListener("paste", onPaste);
  }, [images]);

  // Brief F — consolidated to removeBgForUrl helper (defined above at
   // imageUtils.js:400). Pre-helper inline body did the same five steps
   // verbatim. setBgStatus / setRemovingBg UI state preserved verbatim
   // (DevImageUploader has the same loading-state pattern minus
   // setBgStatus, kept separate per Convention #74 minimal scope).
   // imgsRef.current[idx] kept as the URL source — closure could be
   // stale; ref is the canonical "current images" read site for this
   // component.
  const handleRemoveBg = async (idx) => {
    setRemovingBg(idx);
    setBgStatus("processing");
    try {
      const newUrl = await removeBgForUrl(imgsRef.current[idx], { deleteOriginal: true });
      onChange(imgsRef.current.map((img, i) => i === idx ? newUrl : img));
    } catch (e) {
      globalToast("Background removal failed: " + e.message, "error");
    }
    setRemovingBg(null);
    setBgStatus("");
  };

  const isEmpty = imgs.length === 0;

  return (
    <div>
      {isEmpty && (
        <div
          onClick={() => { if (!uploading) ref.current.click(); }}
          onDragOver={e => { e.preventDefault(); setDrag(true); }}
          onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget)) setDrag(false); }}
          onDrop={e => { e.preventDefault(); setDrag(false); processFiles(e.dataTransfer.files); }}
          style={{
            border: drag ? "1px solid var(--gold)" : "0.5px solid var(--border)",
            background: drag ? "#faf6ee" : "var(--card-bg)",
            padding: "var(--space-3xl) var(--space-xl)",
            textAlign: "center",
            cursor: uploading ? "wait" : "pointer",
            transition: "border-color 0.15s, background 0.15s",
          }}
        >
          <div style={{ fontSize: 'var(--text-2xl)', color: "var(--text-muted)", marginBottom: 'var(--space-md)'}}>&#8593;</div>
          <div style={{ fontSize: 'var(--text-2xs)', letterSpacing: 2, fontWeight: 'var(--weight-semi)', textTransform: "uppercase", color: drag ? "var(--gold)" : "var(--text)", marginBottom: 'var(--space-sm)', fontFamily: "Montserrat, sans-serif" }}>
            {drag ? "RELEASE TO UPLOAD" : uploading ? "UPLOADING..." : "DROP IMAGES HERE"}
          </div>
          <div style={{ fontSize: 'var(--text-2xs)', letterSpacing: 1, color: "var(--text-muted)", textTransform: "uppercase", marginBottom: 'var(--space-md)', fontFamily: "Montserrat, sans-serif" }}>
            {"\u00B7 CLICK TO BROWSE  \u00B7 CTRL+V  \u00B7 DRAG"}
          </div>
          <div style={{ display: "inline-block", fontSize: 'var(--text-2xs)', letterSpacing: 1, color: "var(--text-muted)", border:"var(--border-thin) solid var(--border)", padding: "var(--space-2xs) var(--space-sm)", fontFamily: "Montserrat, sans-serif" }}>
            JPG &middot; PNG &middot; WEBP &middot; MAX 10MB
          </div>
        </div>
      )}

      {!isEmpty && (
        <div
          onDragOver={e => { e.preventDefault(); setDrag(true); }}
          onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget)) setDrag(false); }}
          onDrop={e => { e.preventDefault(); setDrag(false); processFiles(e.dataTransfer.files); }}
          style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 'var(--space-sm)', marginTop: 'var(--space-md)'}}
        >
          {imgs.map((src, i) => (
            <div key={i} style={{
              aspectRatio: "4/3", background: "#fafaf8", border: i === 0 ? "1px solid var(--gold)" : "0.5px solid var(--border)",
              overflow: "hidden", position: "relative",
            }}
            onMouseEnter={() => setHoverIdx(i)} onMouseLeave={() => setHoverIdx(-1)}>
              <img src={src} alt="" loading="lazy" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
              {i === 0 && (
                <span style={{ position: "absolute", top: 4, left: 4, fontSize: 'var(--text-2xs)', letterSpacing: 1.5, fontWeight: 'var(--weight-semi)', background: "var(--gold)", color: "var(--card-bg)", padding: "var(--space-2xs) var(--space-xs)", textTransform: "uppercase", fontFamily: "Montserrat, sans-serif" }}>MAIN</span>
              )}
              {removingBg === i && (
                <div style={{ position: "absolute", inset: 0, background: "rgba(212,175,116,0.7)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <div style={{ color: "var(--card-bg)", fontSize: 'var(--text-2xs)', fontWeight: 'var(--weight-bold)', textAlign: "center", letterSpacing: 1, textTransform: "uppercase", fontFamily: "Montserrat, sans-serif" }}>REMOVING BG...</div>
                </div>
              )}
              <button
                onClick={e => { e.stopPropagation(); if (isStorageUrl(src)) deleteStorageImages([src]); onChange(imgs.filter((_, j) => j !== i)); }}
                style={{
                  position: "absolute", top: 4, right: 4, width: 18, height: 18,
                  background: "var(--text)", color: "var(--card-bg)", fontSize: 'var(--text-2xs)',
                  border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                  opacity: hoverIdx === i ? 1 : 0, transition: "opacity 0.15s", padding: 0, lineHeight: 'var(--lh-tight)',
                }}
              >&times;</button>
              {hoverIdx === i && removingBg === null && (
                <button onClick={e => { e.stopPropagation(); handleRemoveBg(i); }} title="Remove background"
                  style={{ position: "absolute", bottom: 4, left: "50%", transform: "translateX(-50%)", background: "rgba(26,26,24,0.75)", border: "none", padding: "var(--space-2xs) var(--space-sm)", color: "var(--card-bg)", cursor: "pointer", fontSize: 'var(--text-2xs)', fontWeight: 'var(--weight-semi)', letterSpacing: 0.5, textTransform: "uppercase", fontFamily: "Montserrat, sans-serif", whiteSpace: "nowrap" }}>
                  BG
                </button>
              )}
            </div>
          ))}
          {imgs.length < maxImages && (
            <div
              onClick={() => { if (!uploading) ref.current.click(); }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = "var(--gold)"; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = "var(--border)"; }}
              style={{
                aspectRatio: "4/3", border:"var(--border-thin) dashed var(--border)",
                display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 'var(--space-sm)',
                cursor: uploading ? "wait" : "pointer", background: "#fafaf8",
              }}
            >
              <span style={{ fontSize: 'var(--text-lg)', color: "var(--text-muted)", lineHeight: 'var(--lh-tight)' }}>+</span>
              <span style={{ fontSize: 'var(--text-2xs)', textTransform: "uppercase", color: "var(--text-muted)", letterSpacing: 1, fontFamily: "Montserrat, sans-serif" }}>
                {uploading ? "UPLOADING..." : "ADD MORE"}
              </span>
            </div>
          )}
        </div>
      )}

      {bgStatus === "processing" && (
        <div style={{ fontSize: 'var(--text-2xs)', color: "var(--gold)", fontWeight: 'var(--weight-semi)', marginTop: 'var(--space-sm)', textAlign: "center", letterSpacing: 1, textTransform: "uppercase", fontFamily: "Montserrat, sans-serif" }}>REMOVING BACKGROUND...</div>
      )}
      <input ref={ref} type="file" accept="image/jpeg,image/png,image/webp" multiple style={{ display: "none" }} onChange={e => processFiles(e.target.files)} />
      {toast && (
        <div style={{ position: "fixed", bottom: 20, left: "50%", transform: "translateX(-50%)", fontSize: 'var(--text-2xs)', letterSpacing: 2, background: "var(--text)", color: "var(--card-bg)", padding: "var(--space-sm) var(--space-lg)", zIndex: 'var(--z-toast)', fontFamily: "Montserrat, sans-serif", textTransform: "uppercase" }}>{toast}</div>
      )}
    </div>
  );
}
